<template>
  <div
    class="brief-panel"
    :class="{ 'brief-panel--linked': currentBrief && !isSelectingBrief }"
  >
    <!-- Selector: no linked brief / changing brief -->
    <template v-if="!currentBrief || isSelectingBrief">
      <div class="brief-panel__header">
        <div class="brief-panel__title-row">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="brief-panel__title-icon"
            aria-hidden="true"
          >
            <rect
              width="8"
              height="4"
              x="8"
              y="2"
              rx="1"
              ry="1"
            />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <path d="M12 11h4" />
            <path d="M12 16h4" />
            <path d="M8 11h.01" />
            <path d="M8 16h.01" />
          </svg>
          <span class="brief-panel__title-text">Brief</span>
        </div>
      </div>

      <div class="brief-panel__body">
        <div class="brief-panel__action-row">
          <span>Select a brief to link it</span>
          <a
            href="/briefs"
            class="brief-panel__new-brief"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
            New brief
          </a>
        </div>

        <div class="brief-panel__search">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="brief-panel__search-icon"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            v-model.trim="search"
            type="text"
            class="brief-panel__search-input"
            placeholder="Search briefs"
          >
        </div>

        <div class="brief-panel__list-wrap">
          <div class="brief-panel__list">
            <div
              v-if="isLoading"
              class="brief-panel__state"
            >
              Loading briefs…
            </div>

            <div
              v-else-if="filteredBriefs.length === 0"
              class="brief-panel__state"
            >
              No briefs found.
            </div>

            <button
              v-for="brief in filteredBriefs"
              :key="brief.id"
              type="button"
              class="brief-panel__list-item"
              :disabled="isSaving"
              @click="selectBrief(brief)"
            >
              <div class="brief-panel__list-title">
                {{ brief.title || 'Untitled brief' }}
              </div>
              <div class="brief-panel__list-meta">
                {{ briefOwnerLabel(brief) }} · {{ formatStatusLower(brief.status) }}
              </div>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Linked brief view -->
    <template v-else>
      <div class="brief-panel__linked-header">
        <div class="brief-panel__title-row">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="brief-panel__title-icon"
            aria-hidden="true"
          >
            <rect
              width="8"
              height="4"
              x="8"
              y="2"
              rx="1"
              ry="1"
            />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <path d="M12 11h4" />
            <path d="M12 16h4" />
            <path d="M8 11h.01" />
            <path d="M8 16h.01" />
          </svg>
          <span class="brief-panel__title-text">Brief</span>
        </div>

        <div class="brief-panel__linked-actions">
          <a
            :href="briefUrl(currentBrief)"
            class="brief-panel__open-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
            Open full brief
          </a>

          <div
            class="brief-panel__menu"
            @keydown.esc.stop="menuOpen = false"
          >
            <button
              type="button"
              class="brief-panel__menu-trigger"
              aria-label="Brief actions"
              aria-haspopup="menu"
              :aria-expanded="menuOpen ? 'true' : 'false'"
              @click.stop="menuOpen = !menuOpen"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="1"
                />
                <circle
                  cx="19"
                  cy="12"
                  r="1"
                />
                <circle
                  cx="5"
                  cy="12"
                  r="1"
                />
              </svg>
            </button>

            <div
              v-if="menuOpen"
              class="brief-panel__menu-dropdown"
              role="menu"
            >
              <button
                type="button"
                class="brief-panel__menu-item"
                role="menuitem"
                @click="onChangeBrief"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                  <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
                  <line
                    x1="8"
                    x2="16"
                    y1="12"
                    y2="12"
                  />
                </svg>
                Change brief…
              </button>
              <div
                class="brief-panel__menu-divider"
                role="separator"
              />
              <button
                type="button"
                class="brief-panel__menu-item brief-panel__menu-item--danger"
                role="menuitem"
                @click="onUnlinkBrief"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
                  <path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
                  <line
                    x1="8"
                    x2="8"
                    y1="2"
                    y2="5"
                  />
                  <line
                    x1="2"
                    x2="5"
                    y1="8"
                    y2="8"
                  />
                  <line
                    x1="16"
                    x2="16"
                    y1="19"
                    y2="22"
                  />
                  <line
                    x1="19"
                    x2="22"
                    y1="16"
                    y2="16"
                  />
                </svg>
                Unlink brief
              </button>
            </div>
          </div>
        </div>
      </div>

      <BriefContentPanel
        :brief="currentBrief"
        variant="sidebar"
        show-meta
        owner-source="viewer"
      />
    </template>
  </div>
</template>

<script>
import { getLayoutLinkedBrief, linkLayoutToBrief, listBriefs } from '@frontend/components/briefs/briefsApi';
import { briefPath } from '@frontend/components/briefs/briefPath';
import BriefContentPanel from '@frontend/components/briefs/BriefContentPanel.vue';

function normalizeLinkedBrief(value) {
  if (value == null || value === '' || value === 'null') {
    return null;
  }

  let brief = value;
  if (typeof brief === 'string' || brief instanceof String) {
    try {
      brief = JSON.parse(String(brief));
    } catch (error) {
      console.error('Failed to parse linked brief:', error);
      return null;
    }
  }

  if (!brief || typeof brief !== 'object' || Array.isArray(brief) || !brief.id) {
    return null;
  }

  return brief;
}

export default {
  name: 'BriefPanel',
  components: {
    BriefContentPanel,
  },
  props: {
    layoutId: {
      type: [String, Number],
      required: true,
    },
    customerId: {
      type: [String, Number],
      required: true,
    },
    initialLinkedBrief: {
      type: [Object, String],
      default: null,
    },
  },
  data() {
    const linkedBrief = normalizeLinkedBrief(this.initialLinkedBrief);
    return {
      briefs: [],
      search: '',
      isLoading: false,
      isSaving: false,
      isSelectingBrief: !linkedBrief,
      currentBrief: linkedBrief,
      menuOpen: false,
    };
  },
  computed: {
    filteredBriefs() {
      const query = this.search.trim().toLowerCase();
      if (!query) {
        return this.briefs;
      }

      return this.briefs.filter((brief) => {
        const title = String(brief.title || '').toLowerCase();
        const summary = String(brief.summary || '').toLowerCase();
        return title.includes(query) || summary.includes(query);
      });
    },
  },
  created() {
    this.fetchBriefs();
    this.hydrateLinkedBrief();
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    onDocumentClick() {
      this.menuOpen = false;
    },
    async fetchBriefs() {
      this.isLoading = true;
      try {
        const res = await listBriefs({ per_page: 100 }, { internal: true });
        this.briefs = Array.isArray(res?.data) ? res.data : [];
      } catch (error) {
        console.error('Failed to load briefs:', error);
        this.briefs = [];
        if (window.toastr) {
          window.toastr.error('Failed to load briefs.');
        }
      } finally {
        this.isLoading = false;
      }
    },
    // Refresh from the live brief so sidebar matches the full brief (refs + latest HTML).
    // Read-only: the server rebuilds the snapshot from the brief but does not
    // persist it, so the stored copy (and the proof page that reads it) is
    // untouched until someone re-links.
    async hydrateLinkedBrief() {
      if (!this.currentBrief?.id || this.isSelectingBrief) return;
      try {
        const res = await getLayoutLinkedBrief(this.customerId, this.layoutId);
        if (!res?.linked_brief) return;
        this.currentBrief = res.linked_brief;
      } catch (error) {
        console.error('Failed to refresh linked brief:', error);
      }
    },
    briefUrl(briefOrId) {
      return briefPath(briefOrId);
    },
    briefOwnerLabel(brief) {
      if (brief && brief.is_owner !== false) {
        return 'You';
      }
      return brief.creator_name
        || brief.submitter_display_name
        || brief.submitter_name
        || 'You';
    },
    formatStatusLower(status) {
      const value = String(status || 'draft').replace(/_/g, ' ').trim().toLowerCase();
      return value || 'draft';
    },
    onChangeBrief() {
      this.menuOpen = false;
      this.isSelectingBrief = true;
    },
    async onUnlinkBrief() {
      this.menuOpen = false;
      await this.unlinkBrief();
    },
    // Only the id is sent — the server builds and stores the snapshot, and hands
    // it back so the linked view renders without a second fetch. Pass a falsy
    // briefId to unlink.
    async saveLinkedBrief(briefId) {
      this.isSaving = true;
      try {
        const res = await linkLayoutToBrief(this.customerId, this.layoutId, briefId);
        const linkedBrief = res?.linked_brief || null;

        this.currentBrief = linkedBrief;
        this.isSelectingBrief = !linkedBrief;
        this.$emit('updated', linkedBrief);
        if (window.toastr) {
          window.toastr.success(linkedBrief ? 'Brief linked to layout.' : 'Brief unlinked.');
        }
      } catch (error) {
        console.error('Failed to save linked brief:', error);
        if (window.toastr) {
          window.toastr.error('Failed to update linked brief.');
        }
      } finally {
        this.isSaving = false;
      }
    },
    async selectBrief(brief) {
      if (!brief?.id || this.isSaving) return;
      await this.saveLinkedBrief(brief.id);
    },
    async unlinkBrief() {
      if (this.isSaving) return;
      await this.saveLinkedBrief(null);
    },
  },
};
</script>

<style scoped lang="scss">
/* Match mock: beat LayoutSidebar `.b-sidebar * { font-size: .875rem }` */
.brief-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #0f172a;
  font-size: 14px !important;
  line-height: 1.5 !important;
  -webkit-font-smoothing: antialiased;
}

.brief-panel--linked {
  gap: 0;
}

.brief-panel *,
.brief-panel *::before,
.brief-panel *::after {
  box-sizing: border-box;
}

.brief-panel__header {
  margin-bottom: -8px;
}

.brief-panel__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.brief-panel__title-icon {
  width: 16px !important;
  height: 16px !important;
  color: #189a73;
  flex-shrink: 0;
}

.brief-panel__title-text {
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
  color: #0f172a;
}

.brief-panel__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.brief-panel__action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px !important;
  line-height: 16px !important;
  color: #64748b;
}

.brief-panel__new-brief {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 16px !important;
  color: #0f172a;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: #189a73;
    color: #fff;
    text-decoration: none;
  }
}

.brief-panel__search {
  position: relative;
}

.brief-panel__search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  width: 14px !important;
  height: 14px !important;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: none;
}

.brief-panel__search-input {
  display: block;
  width: 100%;
  height: 32px;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-size: 14px !important;
  line-height: 20px !important;
  outline: none;

  &::placeholder {
    color: #64748b;
  }

  &:focus {
    border-color: #189a73;
    box-shadow: 0 0 0 2px rgba(24, 154, 115, 0.2);
  }
}

.brief-panel__list-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.brief-panel__list {
  max-height: 320px;
  overflow-y: auto;
}

.brief-panel__list-item {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover:not(:disabled) {
    background: rgba(241, 245, 249, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.brief-panel__list-title {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.brief-panel__list-meta {
  margin-top: 2px;
  font-size: 12px !important;
  line-height: 16px !important;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.brief-panel__state {
  padding: 16px 12px;
  text-align: center;
  font-size: 12px !important;
  line-height: 16px !important;
  color: #64748b;
}

.brief-panel__linked-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.brief-panel__linked-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.brief-panel__open-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #189a73;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 16px !important;
  text-decoration: none;
  white-space: nowrap;

  svg {
    width: 14px !important;
    height: 14px !important;
    flex-shrink: 0;
  }

  &:hover {
    color: #189a73;
    text-decoration: underline;
  }
}

.brief-panel__menu {
  position: relative;
}

.brief-panel__menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #64748b;
  cursor: pointer;

  svg {
    width: 16px !important;
    height: 16px !important;
  }

  &:hover {
    color: #0f172a;
    background: #f1f5f9;
  }
}

.brief-panel__menu-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 30;
  width: 11rem; // w-44
  min-width: 11rem;
  padding: 4px; // p-1
  border: 1px solid #e2e8f0;
  border-radius: 6px; // rounded-md
  background: #fff;
  box-shadow:
    0 4px 6px -1px rgba(15, 23, 42, 0.1),
    0 2px 4px -2px rgba(15, 23, 42, 0.1);
}

.brief-panel__menu-item {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  padding: 6px 8px; // py-1.5 px-2
  border: 0;
  border-radius: 2px; // rounded-sm
  background: transparent;
  color: #0f172a;
  font-size: 14px !important; // text-sm
  font-weight: 400 !important;
  line-height: 20px !important;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    width: 14px !important; // h-3.5 w-3.5
    height: 14px !important;
    margin-right: 8px; // mr-2
    flex-shrink: 0;
    color: currentColor;
  }

  &:hover {
    background: #189a73;
    color: #fff;
  }
}

.brief-panel__menu-item--danger {
  color: #ef4444;

  &:hover {
    background: #189a73;
    color: #fff;
  }
}

.brief-panel__menu-divider {
  height: 1px;
  margin: 4px -4px; // -mx-1 my-1
  background: #e2e8f0;
}

</style>
